import { useState } from 'react';
import { Modal } from 'reactstrap';
import { IconCalendarPlus, IconCheck, IconMapPin } from '@tabler/icons-react';
import { Chips } from './PreSaleVisitFeedbackModal';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useProjectList from '../../../hooks/useProjectList';
import useProperList from '../../../hooks/useProperList';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { listToSelectOptionAdapter } from '../../../adapters/listAdapter';
import {
  createPropertyVisitsEndpoint,
  getAvailableTimeVisitsEndpoint,
  getPropertiesEndpoint,
  getTenantByMailEndpoint,
} from '../../properties/services/propertyService';
import { getLandlordByMailEndpoint } from '../../../services/landlordService';
import {
  createProjectVisitsEndpoint,
  getAvailableTimeProjectVisitsEndpoint,
} from '../../investors/services/visitsInvestorService';
import useOfficeAddress from '../../investors/hooks/useOfficeAddress';
import useProject from '../../investors/hooks/useProject';
import {
  VISIT_LOCATION_OFFICE,
  VISIT_TYPE_PRESENTIAL,
  visitLocationOptions,
  visitTypeOptions,
} from '../../investors/constants/visitConstants';
import { getVisitAddress } from '../../investors/helpers/visitAddressHelper';
import { LeadMarketplaceType } from '../../../components/VisitMarketplaceTypeBadge';
import { activeTab } from '../types/dashboard.types';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  // Renta/Secondary agendan sobre una propiedad; PreVenta, sobre un proyecto.
  tab: Extract<activeTab, 'rent' | 'secondary' | 'pre-sale'>;
}

interface PropertyResult {
  id: string;
  code: number;
  address: string;
}

interface HourOption {
  value: number;
  label: string;
  disabled?: boolean;
}

// react-select con el estilo de los campos del modal.
const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    borderColor: state.isFocused ? '#1a9e5c' : '#d5dae1',
    boxShadow: 'none',
    '&:hover': { borderColor: '#1a9e5c' },
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#1a9e5c'
      : state.isFocused
        ? '#e6f6ee'
        : '#fff',
    color: state.isSelected ? '#fff' : state.isDisabled ? '#9ca3af' : '#1f2937',
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
};

const today = () => new Date().toISOString().slice(0, 10);

// Crea una visita (Renta/Secondary) o asesoría (PreVenta) desde el dashboard,
// con los mismos campos y endpoints que los calendarios de cada flujo.
const CreateVisitModal = ({ isOpen, toggle, tab }: Props) => {
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { callEndpoint, isLoading } = useCallApiAndLoad();
  const isPreSale = tab === 'pre-sale';
  const personLabel = isPreSale ? 'inversionista' : 'inquilino';

  // Propers según el tipo de negocio de la pestaña.
  const { propersOptions } = useProperList(
    isPreSale ? 'for_pre_sale' : tab === 'rent' ? 'for_rent' : 'for_sale',
  );
  const { projects } = useProjectList();
  const { officeAddress } = useOfficeAddress();
  const { doProjectInfo } = useProject();

  // Propiedad (Renta/Secondary) o proyecto (PreVenta)
  const [code, setCode] = useState('');
  const [results, setResults] = useState<PropertyResult[] | null>(null);
  const [property, setProperty] = useState<PropertyResult | null>(null);
  const [project, setProject] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [projectAddress, setProjectAddress] = useState('');
  const targetId = isPreSale ? project?.id : property?.id;

  // Inquilino / inversionista
  const [email, setEmail] = useState('');
  const [person, setPerson] = useState<any>(null);

  // Fecha, hora, proper y (PreVenta) tipo y lugar
  const [date, setDate] = useState('');
  const [hours, setHours] = useState<HourOption[]>([]);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [properId, setProperId] = useState('');
  const [visitType, setVisitType] = useState('virtual');
  const [location, setLocation] = useState(VISIT_LOCATION_OFFICE);

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const close = () => {
    toggle();
  };

  const searchProperty = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Ingresa el código de la propiedad');
      return;
    }
    setError('');
    const { status, data } = await callEndpoint(
      getPropertiesEndpoint(`?code=${encodeURIComponent(trimmed)}`, token),
    );
    if (status === 200) {
      setResults(fetchResponseAdapter(data).data?.items || []);
    } else {
      setResults(null);
      setError('No se pudo buscar la propiedad');
    }
  };

  const loadHours = async (targetId: string, day: string) => {
    setHours([]);
    setStartTime(null);
    if (!targetId || !day) return;
    const endpoint = isPreSale
      ? getAvailableTimeProjectVisitsEndpoint(targetId, day, token)
      : getAvailableTimeVisitsEndpoint(targetId, day, token);
    const { status, data } = await callEndpoint(endpoint);
    if (status === 200) {
      setHours(
        listToSelectOptionAdapter(
          fetchResponseAdapter(data).data,
          'minutes',
          'time',
          true,
          'is_blocked',
        ) as HourOption[],
      );
    }
  };

  const selectProject = async (selected: { id: string; name: string }) => {
    setProject(selected);
    setProjectAddress('');
    if (date) loadHours(selected.id, date);
    try {
      const detail = await doProjectInfo(selected.id);
      setProjectAddress(detail?.address || '');
    } catch {
      setProjectAddress('');
    }
  };

  const selectProperty = (selected: PropertyResult) => {
    setProperty(selected);
    if (date) loadHours(selected.id, date);
  };

  const onDateChange = (value: string) => {
    setDate(value);
    if (targetId) loadHours(targetId, value);
  };

  const searchPerson = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Debes ingresar un correo válido');
      setPerson(null);
      return;
    }
    setError('');
    const { status, data } = await callEndpoint(
      isPreSale
        ? getLandlordByMailEndpoint(trimmed, token)
        : getTenantByMailEndpoint(trimmed, token),
    );
    if (status === 200) {
      setPerson(fetchResponseAdapter(data).data);
      return;
    }
    setError(`${isPreSale ? 'Inversionista' : 'Inquilino'} no encontrado`);
    setPerson(null);
  };

  const submit = async () => {
    const personId = isPreSale ? person?.landlord_id : person?.tenant_id;
    if (!targetId) {
      setError(isPreSale ? 'Selecciona un proyecto' : 'Selecciona una propiedad');
      return;
    }
    if (!personId) {
      setError(`Busca y selecciona al ${personLabel}`);
      return;
    }
    if (!date) {
      setError('Selecciona la fecha');
      return;
    }
    if (startTime === null) {
      setError('Selecciona la hora');
      return;
    }
    if (!properId) {
      setError('Selecciona un proper');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const endpoint = isPreSale
        ? createProjectVisitsEndpoint(
            {
              project_id: targetId,
              landlord_id: personId,
              date,
              start_time: startTime,
              visit_type: visitType,
              location: getVisitAddress(
                visitType,
                location,
                officeAddress,
                projectAddress,
              ),
              proper_id: properId,
            },
            token,
          )
        : createPropertyVisitsEndpoint(
            {
              property_id: targetId,
              tenant_id: personId,
              date,
              start_time: startTime,
              proper_id: properId,
              marketplace_type:
                tab === 'rent'
                  ? LeadMarketplaceType.ON_RENT
                  : LeadMarketplaceType.ON_SALE,
            },
            token,
          );
      const { status } = await callEndpoint(endpoint);
      if (status === 201) {
        close();
        navigate(0);
        return;
      }
      setError(`(${status}) - No fue posible agendar la visita`);
    } finally {
      setSaving(false);
    }
  };

  const title = isPreSale ? 'Crear asesoría' : 'Crear visita';
  const presential = isPreSale && visitType === VISIT_TYPE_PRESENTIAL;
  const shownAddress = presential
    ? location === VISIT_LOCATION_OFFICE
      ? officeAddress?.address
      : projectAddress
    : '';

  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : close} centered className="vfm">
      <div className="vfm-body">
        <div className="vfm-head">
          <span className="vfm-icon">
            <IconCalendarPlus size={24} />
          </span>
          <div>
            <h2 className="vfm-title">{title}</h2>
            <p className="vfm-sub">
              {isPreSale
                ? 'Agenda una asesoría para un inversionista.'
                : `Agenda una visita de ${tab === 'rent' ? 'renta' : 'venta'} para un inquilino.`}
            </p>
          </div>
        </div>

        {/* Proyecto o propiedad */}
        {isPreSale ? (
          <div className="vfm-field">
            <label>
              Proyecto <b>*</b>
            </label>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              placeholder="Selecciona un proyecto"
              options={projects.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              onChange={(selected: any) =>
                selected?.value &&
                selectProject({ id: selected.value, name: selected.label })
              }
            />
          </div>
        ) : (
          <div className="vfm-field">
            <label>
              Código de propiedad <b>*</b>
            </label>
            <div className="vfm-search">
              <input
                className="vfm-input"
                type="number"
                placeholder="Ej. 1400"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchProperty()}
              />
              <button
                type="button"
                className="vfm-btn vfm-btn--ghost"
                onClick={searchProperty}
                disabled={isLoading}
              >
                Buscar
              </button>
            </div>
            {results && results.length === 0 && (
              <small className="vfm-hint">
                No se encontró ninguna propiedad con ese código.
              </small>
            )}
            {results?.map((item) => (
              <button
                type="button"
                key={item.id}
                className={`vfm-result${property?.id === item.id ? ' is-active' : ''}`}
                onClick={() => selectProperty(item)}
              >
                <span>
                  <b>[{item.code}]</b> {item.address}
                </span>
                {property?.id === item.id && <IconCheck size={16} />}
              </button>
            ))}
          </div>
        )}

        {/* Inquilino / inversionista */}
        <div className="vfm-field">
          <label>
            Asignar {personLabel} <b>*</b>
          </label>
          <div className="vfm-search">
            <input
              className="vfm-input"
              type="text"
              placeholder={`Correo de ${personLabel}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchPerson()}
            />
            <button
              type="button"
              className="vfm-btn vfm-btn--ghost"
              onClick={searchPerson}
              disabled={isLoading}
            >
              Buscar
            </button>
          </div>
          {person && (
            <small className="vfm-hint vfm-hint--ok">
              <IconCheck size={14} /> {person.name}
              {person.phone ? ` · ${person.phone}` : ''}
            </small>
          )}
        </div>

        {/* Fecha y hora */}
        <div className="vfm-row">
          <div className="vfm-field">
            <label>
              Fecha <b>*</b>
            </label>
            <input
              className="vfm-input"
              type="date"
              min={today()}
              value={date}
              onChange={(e) => onDateChange(e.target.value)}
            />
          </div>
          <div className="vfm-field">
            <label>
              Hora <b>*</b>
            </label>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              key={`hours-${targetId}-${date}-${hours.length}`}
              placeholder={
                targetId && date
                  ? 'Selecciona una hora'
                  : `Elige ${isPreSale ? 'proyecto' : 'propiedad'} y fecha`
              }
              options={hours}
              isOptionDisabled={(option: any) => option.disabled}
              onChange={(selected: any) => setStartTime(selected.value)}
            />
          </div>
        </div>

        {/* Tipo de asesoría y lugar (sólo PreVenta) */}
        {isPreSale && (
          <div className="vfm-field">
            <label>
              Tipo de asesoría <b>*</b>
            </label>
            <Chips
              options={visitTypeOptions}
              value={[visitType]}
              onToggle={setVisitType}
            />
          </div>
        )}
        {presential && (
          <div className="vfm-field">
            <label>
              Lugar de la asesoría <b>*</b>
            </label>
            <Chips
              options={visitLocationOptions}
              value={[location]}
              onToggle={setLocation}
            />
            {shownAddress && (
              <small className="vfm-hint">
                <IconMapPin size={14} /> {shownAddress}
              </small>
            )}
          </div>
        )}

        {/* Proper */}
        <div className="vfm-field">
          <label>
            Proper <b>*</b>
          </label>
          <Select
            styles={selectStyles}
              menuPortalTarget={document.body}
            key={`propers-${propersOptions.length}`}
            placeholder="Selecciona un proper"
            options={propersOptions}
            onChange={(selected: any) => setProperId(selected.value)}
          />
        </div>

        {error && <div className="vfm-error vfm-error--box">{error}</div>}

        <div className="vfm-footer">
          <button
            type="button"
            className="vfm-btn vfm-btn--ghost"
            onClick={close}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="vfm-btn vfm-btn--primary"
            onClick={submit}
            disabled={saving || isLoading}
          >
            {saving ? 'Guardando…' : title}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateVisitModal;
